import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { Plus, Trash, GridFour, Kanban, Table as TableIcon, X, ArrowUp, CalendarBlank, Calendar, CaretLeft, CaretRight, Stack, ImageSquare, FileArrowUp, Check } from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmProvider";
import BulkImportModal from "@/components/BulkImportModal";
import TemplatesModal from "@/components/TemplatesModal";

const DEFAULT_STATUSES = ["Idea","Drafting","Ready","Approved","Scheduled","Published"];
const STATUS_LABELS_KEY = "bl_status_labels";
const DEFAULT_POST_TYPE_TAGS = ["Attract", "Nurture", "Position", "Convert"];
const CUSTOM_TAGS_KEY = "bl_custom_post_type_tags";

function loadLabels() {
  try { return JSON.parse(localStorage.getItem(STATUS_LABELS_KEY) || "{}"); } catch { return {}; }
}
function saveLabels(map) { localStorage.setItem(STATUS_LABELS_KEY, JSON.stringify(map)); }
function loadCustomTags() {
  try { return JSON.parse(localStorage.getItem(CUSTOM_TAGS_KEY) || "[]"); } catch { return []; }
}
function saveCustomTags(arr) { localStorage.setItem(CUSTOM_TAGS_KEY, JSON.stringify(arr)); }

function toISODate(d) {
  // Returns YYYY-MM-DD in local time
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Normalize pasted text: fix CRLF, strip trailing spaces per line,
// and collapse 3+ consecutive newlines into a single blank line.
export function normalizePastedText(s) {
  if (!s) return s;
  return s
    .replace(/\r\n?/g, "\n")     // CRLF or CR → LF
    .replace(/[ \t]+\n/g, "\n")  // trailing spaces before newlines
    .replace(/\n{3,}/g, "\n\n"); // collapse 3+ blank lines into one
}

// Attach as onPaste to any textarea/input to auto-clean line-break spam
// when users paste in copy from LinkedIn / Google Docs / Notion.
export function onPasteNormalize(setValue) {
  return (e) => {
    const el = e.currentTarget;
    const pasted = e.clipboardData?.getData("text");
    if (pasted == null) return;
    const cleaned = normalizePastedText(pasted);
    if (cleaned === pasted) return; // nothing changed, let the browser handle it normally
    e.preventDefault();
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const before = el.value.slice(0, start);
    const after = el.value.slice(end);
    const next = before + cleaned + after;
    setValue(next);
    // Restore cursor position after React re-render
    requestAnimationFrame(() => {
      try {
        const pos = start + cleaned.length;
        el.setSelectionRange(pos, pos);
      } catch {}
    });
  };
}

function parseISODate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

const TAG_COLOR = {
  attract:  { bg: "#FEE3D8", fg: "#B14818" },
  nurture:  { bg: "#E4EFDD", fg: "#3C6B23" },
  position: { bg: "#E3E7F5", fg: "#334095" },
  convert:  { bg: "#F7DFEB", fg: "#8A2E63" },
};
function tagStyle(tag) {
  const k = (tag || "").toLowerCase();
  const c = TAG_COLOR[k];
  if (c) return { background: c.bg, color: c.fg, borderColor: "transparent" };
  return { background: "var(--paper)", color: "var(--ink-2)" };
}

// Cross-posting channels. Users can pick any subset per post; the calendar card
// shows a small colored pill for each. Order below drives the picker order.
const DEFAULT_CHANNELS = [
  { key: "LinkedIn",  label: "LinkedIn",  short: "Li", bg: "#0A66C2", fg: "#fff" },
  { key: "X",         label: "X",         short: "X",  bg: "#0F1419", fg: "#fff" },
  { key: "YouTube",   label: "YouTube",   short: "YT", bg: "#FF0000", fg: "#fff" },
  { key: "Substack",  label: "Substack",  short: "Su", bg: "#FF6719", fg: "#fff" },
  { key: "Reddit",    label: "Reddit",    short: "Re", bg: "#FF4500", fg: "#fff" },
  { key: "Instagram", label: "Instagram", short: "Ig", bg: "#E4405F", fg: "#fff" },
  { key: "TikTok",    label: "TikTok",    short: "Tt", bg: "#010101", fg: "#fff" },
  { key: "Threads",   label: "Threads",   short: "Th", bg: "#101010", fg: "#fff" },
];
const CHANNEL_LOOKUP = Object.fromEntries(DEFAULT_CHANNELS.map(c => [c.key.toLowerCase(), c]));
function channelStyle(key) {
  const c = CHANNEL_LOOKUP[(key || "").toLowerCase()];
  if (c) return { background: c.bg, color: c.fg, borderColor: "transparent" };
  return { background: "var(--paper)", color: "var(--ink-2)" };
}
function channelShort(key) {
  const c = CHANNEL_LOOKUP[(key || "").toLowerCase()];
  return c?.short || (key || "").slice(0, 2);
}
function ChannelChips({ channels, size = "sm" }) {
  if (!channels || !channels.length) return null;
  const px = size === "sm" ? "3px 6px" : "3px 8px";
  const fs = size === "sm" ? 10 : 11;
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      {channels.map(ch => (
        <span
          key={ch}
          className="chip inline-flex items-center font-mono"
          style={{ padding: px, fontSize: fs, ...channelStyle(ch) }}
          title={ch}
        >{channelShort(ch)}</span>
      ))}
    </span>
  );
}

export default function PlannerPage() {
  const confirm = useConfirm();
  const [posts, setPosts] = useState([]);
  const [view, setView] = useState("grid");
  const [selected, setSelected] = useState(null);
  const [pillars, setPillars] = useState([]);
  const [labels, setLabels] = useState(loadLabels());
  const [customTags, setCustomTags] = useState(loadCustomTags());
  const [sortByPriority, setSortByPriority] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all"); // 'all' | 'drafts' | specific status
  const [dragId, setDragId] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [backlogOpen, setBacklogOpen] = useState(true);
  const [popupPost, setPopupPost] = useState(null); // backlog / grid popup with prev/next
  const [popupList, setPopupList] = useState([]);   // list to navigate through (backlog or grid subset)
  const [showBulk, setShowBulk] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  const STATUSES = DEFAULT_STATUSES;
  const displayLabel = (s) => labels[s] || s;
  const allTagOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const t of [...DEFAULT_POST_TYPE_TAGS, ...customTags]) {
      const key = t.trim();
      if (!key) continue;
      const lk = key.toLowerCase();
      if (seen.has(lk)) continue;
      seen.add(lk);
      out.push(key);
    }
    return out;
  }, [customTags]);

  const addCustomTag = (t) => {
    const clean = (t || "").trim();
    if (!clean) return;
    const lk = clean.toLowerCase();
    if (allTagOptions.some(x => x.toLowerCase() === lk)) return;
    const next = [...customTags, clean];
    setCustomTags(next);
    saveCustomTags(next);
  };

  const load = async () => { try { const r = await api.get("/posts"); setPosts(r.data); } finally { setLoaded(true); } };
  const [loaded, setLoaded] = useState(false);
  useEffect(()=>{ load(); (async ()=>{ try { const r = await api.get("/identity"); const arr = r.data.pillars || []; setPillars(arr); window.__blPillars = arr; } catch {} })(); }, []);

  // Shared media cache — used by Calendar cards AND the Backlog panel so every
  // image-carrying post shows its real thumbnail, not the placeholder icon.
  // The list endpoint strips base64 media_url to keep responses lightweight; we
  // batch-fetch the real image data here and merge it into the cache once.
  const [mediaCache, setMediaCache] = useState({}); // { postId: dataUrl }
  const inFlightRef = useRef(new Set());
  useEffect(() => {
    const missing = posts
      .filter(p => p.has_image && !p.media_url && !mediaCache[p.id] && !inFlightRef.current.has(p.id))
      .map(p => p.id);
    if (missing.length === 0) return;
    // Split into chunks of 60 so the backend's per-request cap can't reject the batch.
    const chunks = [];
    for (let i = 0; i < missing.length; i += 60) chunks.push(missing.slice(i, i + 60));
    missing.forEach(id => inFlightRef.current.add(id));
    (async () => {
      for (const ids of chunks) {
        try {
          const r = await api.post("/posts/media/batch", { ids });
          if (r.data && typeof r.data === "object") {
            setMediaCache(prev => ({ ...prev, ...r.data }));
          }
        } catch {}
        finally { ids.forEach(id => inFlightRef.current.delete(id)); }
      }
    })();
  }, [posts]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (defaults = {}) => {
    // New posts live in-memory until the user actually saves something meaningful.
    // This prevents accidentally-created empty posts from cluttering the DB / list
    // when the user opens the drawer and closes it without typing anything.
    const draft = {
      id: null,
      topic: "",
      content: "",
      status: "Idea",
      pillar: "",
      post_type_tag: "",
      priority: 3,
      date_to_post: "",
      media_url: "",
      media_type: "",
      channels: [],
      impressions: 0, reactions: 0, comments: 0, saves: 0,
      _isNew: true,
      ...defaults,
    };
    setSelected(draft);
  };

  // Only these fields are accepted by the backend PostIn model. Whitelisting
  // avoids accidentally serialising React SyntheticEvent props or internal UI
  // flags (_isNew, has_image, etc) that some tooling can splat onto state and
  // that would otherwise crash axios' JSON serialiser with a circular ref.
  const POST_FIELDS = [
    "topic","content","pillar","status","date_to_post","checklist","team_member",
    "media_type","media_url","first_comment",
    "impressions","reactions","comments","saves",
    "priority","post_type_tag","channels","is_placeholder",
  ];
  const cleanPostBody = (obj) => {
    const out = {};
    for (const k of POST_FIELDS) {
      if (obj[k] !== undefined) out[k] = obj[k];
    }
    return out;
  };

  const save = async (updated) => {
    try {
      const isNew = !!(updated?._isNew || !updated?.id);
      const body = cleanPostBody(updated || {});
      // Any manual save commits real content, so it can no longer be a placeholder.
      if (updated?.is_placeholder) body.is_placeholder = false;
      if (isNew) {
        const r = await api.post("/posts", body);
        setPosts(p => [r.data, ...p]);
        setSelected(r.data);
        toast.success("Post created.");
        return r.data;
      }
      const r = await api.put(`/posts/${updated.id}`, body);
      setPosts(list => list.map(p => p.id === updated.id ? r.data : p));
      setSelected(r.data);
      // Also update the popup state if the same post is currently shown there,
      // so the "Save changes" button re-flips to "Saved" without needing a second click.
      setPopupPost(prev => (prev && prev.id === updated.id ? r.data : prev));
      toast.success("Post updated.");
      return r.data;
    } catch (e) {
      console.error("[planner.save] failed:", e?.response?.status, e?.response?.data, e);
      toast.error(formatApiError(e));
      return null;
    }
  };

  const patch = async (id, patchBody) => {
    // Optimistic update
    setPosts(list => list.map(p => p.id === id ? { ...p, ...patchBody } : p));
    try {
      const r = await api.patch(`/posts/${id}`, patchBody);
      setPosts(list => list.map(p => p.id === id ? r.data : p));
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  const remove = async (id) => {
    // A brand-new unsaved draft doesn't exist in the DB yet — just discard locally.
    if (!id) { setSelected(null); return; }
    const p = posts.find(x => x.id === id);
    const label = p?.topic || (p?.content || "").slice(0, 60) || "This post";
    const ok = await confirm({
      title: "Delete this post?",
      description: `"${label}" will be moved to Trash. You can restore it anytime from there.`,
      confirmText: "Move to Trash",
    });
    if (!ok) return;
    await api.delete(`/posts/${id}`);
    toast.success("Moved to Trash. Restore anytime.");
    setSelected(null);
    load();
  };

  const openWithFullImage = async (p, setter) => {
    // Lazy-load base64 image on demand. List endpoint strips it for performance.
    if (p.has_image && !p.media_url) {
      try { const r = await api.get(`/posts/${p.id}`); setter(r.data); return; } catch {}
    }
    setter(p);
  };

  const filteredPosts = useMemo(() => {
    if (statusFilter === "all") return posts;
    if (statusFilter === "drafts") return posts.filter(p => p.status === "Idea" || p.status === "Drafting");
    return posts.filter(p => p.status === statusFilter);
  }, [posts, statusFilter]);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(STATUSES.map(s => [s, []]));
    filteredPosts.forEach(p => (g[p.status] || (g[p.status] = [])).push(p));
    return g;
  }, [filteredPosts]);

  const sortedPosts = useMemo(() => {
    if (!sortByPriority) return filteredPosts;
    return [...filteredPosts].sort((a, b) => (a.priority || 3) - (b.priority || 3));
  }, [filteredPosts, sortByPriority]);

  const draftsCount = useMemo(() => posts.filter(p => p.status === "Idea" || p.status === "Drafting").length, [posts]);

  const dropOnColumn = async (e, status) => {
    const id = dragId || (e.dataTransfer && e.dataTransfer.getData("text/post-id"));
    if (!id) return;
    const p = posts.find(x => x.id === id);
    if (!p || p.status === status) { setDragId(null); return; }
    setDragId(null);
    await patch(p.id, { status });
  };

  const renameStatus = (s) => {
    const cur = labels[s] || s;
    const next = window.prompt(`Rename '${s}' column display label:`, cur);
    if (next == null) return;
    const nextMap = { ...labels, [s]: next.trim() || s };
    setLabels(nextMap); saveLabels(nextMap);
  };

  return (
    <div>
      {view !== 'calendar' && (
        <PageHeader
          eyebrow="Content planner"
          title="Ship the loop."
          subtitle="Idea → Drafting → Ready → Approved → Scheduled → Published. Toggle Grid / Kanban / Table / Calendar."
          action={
            <div className="flex items-center gap-2">
              <button data-testid="planner-bulk-import" className="btn btn-ghost" onClick={()=>setShowBulk(true)}><FileArrowUp size={14}/> Import</button>
              <button data-testid="planner-add-btn" className="btn btn-accent" onClick={add}><Plus size={16}/> New post</button>
            </div>
          }
        />
      )}
      {view === 'calendar' && (
        // Slim inline header on the calendar so the month grid gets more vertical
        // real estate — the huge serif hero was cramping the calendar.
        <div className="mb-3 flex items-center justify-between gap-4 flex-wrap" data-testid="planner-slim-header">
          <div className="flex items-baseline gap-3">
            <div className="eyebrow" style={{color:'var(--ink-3)'}}>Content planner</div>
            <h1 className="text-xl font-serif font-semibold" style={{lineHeight:1.1}}>Calendar</h1>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="planner-templates-btn" className="btn btn-ghost btn-sm" onClick={()=>setShowTemplates(true)} title="Auto-fill your calendar with a repeating cadence"><Calendar size={14}/> Templates</button>
            <button data-testid="planner-bulk-import" className="btn btn-ghost btn-sm" onClick={()=>setShowBulk(true)}><FileArrowUp size={14}/> Import</button>
            <button data-testid="planner-add-btn" className="btn btn-accent btn-sm" onClick={add}><Plus size={16}/> New post</button>
          </div>
        </div>
      )}

      <div className={`${view === 'calendar' ? 'mb-3' : 'mb-6'} flex items-center gap-3 flex-wrap`}>
        <div className="seg">
          <ViewBtn testid="view-grid" active={view==='grid'} onClick={()=>setView('grid')}><GridFour size={13}/> Grid</ViewBtn>
          <ViewBtn testid="view-kanban" active={view==='kanban'} onClick={()=>setView('kanban')}><Kanban size={13}/> Kanban</ViewBtn>
          <ViewBtn testid="view-table" active={view==='table'} onClick={()=>setView('table')}><TableIcon size={13}/> Table</ViewBtn>
          <ViewBtn testid="view-calendar" active={view==='calendar'} onClick={()=>setView('calendar')}><CalendarBlank size={13}/> Calendar</ViewBtn>
        </div>
        {view !== 'calendar' && (
          <>
            <div className="flex items-center gap-1.5" data-testid="status-filters">
              <button
                data-testid="filter-all"
                onClick={()=>setStatusFilter('all')}
                className="chip cursor-pointer"
                style={statusFilter==='all' ? {background:'var(--accent)', color:'#fff', borderColor:'var(--accent)'} : {}}
              >All · {posts.length}</button>
              <button
                data-testid="filter-drafts"
                onClick={()=>setStatusFilter('drafts')}
                className="chip cursor-pointer"
                style={statusFilter==='drafts' ? {background:'var(--accent)', color:'#fff', borderColor:'var(--accent)'} : {}}
              >Drafts only · {draftsCount}</button>
            </div>
            <label className="chip cursor-pointer flex items-center gap-2" data-testid="sort-priority">
              <input type="checkbox" checked={sortByPriority} onChange={e=>setSortByPriority(e.target.checked)}/>
              <ArrowUp size={12}/> Sort by priority
            </label>
          </>
        )}
        {view === 'calendar' && (
          <button data-testid="toggle-backlog" onClick={()=>setBacklogOpen(o=>!o)} className="chip cursor-pointer flex items-center gap-1.5" style={backlogOpen ? {background:'var(--accent)', color:'#fff', borderColor:'var(--accent)'} : {}}>
            <Stack size={12}/> {backlogOpen ? "Hide backlog" : "Show backlog"}
          </button>
        )}
      </div>

      {view === 'grid' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {sortedPosts.map(p => {
            const imgSrc = p.media_url || mediaCache[p.id] || "";
            return (
            <button key={p.id} data-testid={`tile-${p.id}`} onClick={()=>openWithFullImage(p, setSelected)} className="tile p-3 text-left fade-up">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs eyebrow">{p.pillar || "-"}</div>
                <span className="text-[10px] font-mono chip" style={{padding:'2px 8px'}}>P{p.priority || 3}</span>
              </div>
              {imgSrc ? <img src={imgSrc} alt="" className="w-full h-24 object-cover mb-2 rounded-xl"/> : p.has_image ? <div className="w-full h-24 mb-2 rounded-xl animate-pulse" style={{background:'var(--paper)'}}/> : null}
              <div className="text-sm line-clamp-3 flex-1">{p.content || p.topic || "Untitled"}</div>
              <div className="mt-2 flex justify-between items-end gap-1 flex-wrap">
                <div className="flex items-center gap-1 flex-wrap">
                  <span className={`chip status-${p.status}`}>{displayLabel(p.status)}</span>
                  {p.post_type_tag && <span className="chip text-[10px]" style={{padding:'2px 8px', ...tagStyle(p.post_type_tag)}}>{p.post_type_tag}</span>}
                  <ChannelChips channels={p.channels} size="sm"/>
                </div>
                {p.date_to_post && <span className="text-[10px] font-mono" style={{color:'var(--ink-3)'}}>{p.date_to_post}</span>}
              </div>
            </button>
          );})}
          {posts.length === 0 && !loaded && (
            <div className="col-span-full text-center py-16 text-sm" style={{color:'var(--ink-3)'}} data-testid="planner-loading">
              <span className="inline-block w-4 h-4 rounded-full mr-2 align-middle animate-pulse" style={{background:'var(--accent)'}}/>
              Loading posts…
            </div>
          )}
          {posts.length === 0 && loaded && (
            <div className="col-span-full text-center py-16 text-sm" style={{color:'var(--ink-3)'}} data-testid="planner-empty">
              No posts yet. Click "New post".
            </div>
          )}
        </div>
      )}

      {view === 'kanban' && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 overflow-x-auto">
          {STATUSES.map(s => (
            <div key={s} className="card p-3 min-h-[300px]" data-testid={`col-${s}`}
                 onDragOver={(e)=>e.preventDefault()}
                 onDrop={(e)=>dropOnColumn(e, s)}>
              <div className="eyebrow mb-3 flex justify-between items-center">
                <button onClick={()=>renameStatus(s)} title="Rename column" className="hover:underline">{displayLabel(s)}</button>
                <span>{grouped[s].length}</span>
              </div>
              <div className="space-y-2">
                {grouped[s].map(p => (
                  <div key={p.id} data-testid={`kanban-${p.id}`}
                       draggable
                       onDragStart={(e)=>{ setDragId(p.id); try { e.dataTransfer.setData("text/post-id", p.id); e.dataTransfer.effectAllowed = "move"; } catch {} }}
                       onDragEnd={()=>setDragId(null)}
                       onClick={()=>openWithFullImage(p, setSelected)}
                       className="w-full text-left p-3 text-xs rounded-2xl cursor-move fade-up"
                       style={{ background: dragId===p.id ? "var(--accent-tint)" : "var(--bg-soft)", border: "1px solid var(--line)" }}>
                    <div className="line-clamp-3">{p.content || p.topic || "Untitled"}</div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {p.pillar && <span className="chip" style={{padding:'2px 8px', fontSize:10}}>{p.pillar}</span>}
                      {p.post_type_tag && <span className="chip" style={{padding:'2px 8px', fontSize:10, ...tagStyle(p.post_type_tag)}}>{p.post_type_tag}</span>}
                      <span className="chip" style={{padding:'2px 8px', fontSize:10}}>P{p.priority || 3}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {view === 'table' && (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="">
                {["Topic","Pillar","Type","Status","Date to post","Impressions","Reactions","Comments","Saves",""].map(h=><th key={h} className="text-left px-4 py-3 eyebrow">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {posts.map(p=>(
                <tr key={p.id} className=" hover:bg-[color:var(--paper)]">
                  <td className="px-4 py-3 max-w-[300px] truncate"><button className="link-underline" onClick={()=>openWithFullImage(p, setSelected)} data-testid={`row-${p.id}`}>{p.topic || p.content.slice(0,50) || "Untitled"}</button></td>
                  <td className="px-4 py-3">{p.pillar || "-"}</td>
                  <td className="px-4 py-3">{p.post_type_tag ? <span className="chip text-[10px]" style={{padding:'2px 8px', ...tagStyle(p.post_type_tag)}}>{p.post_type_tag}</span> : <span style={{color:'var(--ink-3)'}}>-</span>}</td>
                  <td className="px-4 py-3"><span className={`chip status-${p.status}`}>{p.status}</span></td>
                  <td className="px-4 py-3 font-mono text-xs">{p.date_to_post || "-"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{p.impressions}</td>
                  <td className="px-4 py-3 font-mono text-xs">{p.reactions}</td>
                  <td className="px-4 py-3 font-mono text-xs">{p.comments}</td>
                  <td className="px-4 py-3 font-mono text-xs">{p.saves}</td>
                  <td className="px-4 py-3"><button onClick={()=>remove(p.id)} className="p-1"><Trash size={14}/></button></td>
                </tr>
              ))}
              {posts.length === 0 && <tr><td colSpan="10" className="text-center py-12 text-sm" style={{color:'var(--ink-3)'}}>No posts yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {view === 'calendar' && (
        <CalendarView
          posts={posts}
          month={calendarMonth}
          setMonth={setCalendarMonth}
          backlogOpen={backlogOpen}
          setBacklogOpen={setBacklogOpen}
          dragId={dragId}
          setDragId={setDragId}
          onSelect={(p) => { setPopupPost(null); setPopupList([]); openWithFullImage(p, setSelected); }}
          onOpenPopup={async (p, list) => {
            setSelected(null);
            setPopupList(list);
            await openWithFullImage(p, setPopupPost);
          }}
          onPatch={patch}
          onAdd={add}
          displayLabel={displayLabel}
          mediaCache={mediaCache}
        />
      )}

      {selected && (
        <PostDrawer
          post={selected}
          onClose={()=>setSelected(null)}
          onSave={async (p, opts = {}) => {
            const saved = await save(p);
            // Only close the drawer on a successful save — otherwise the user
            // silently loses their unsaved edits when the API returns an error.
            if (saved && !opts.keepOpen) setSelected(null);
            return saved;
          }}
          onDelete={()=>remove(selected.id)}
          tagOptions={allTagOptions}
          onAddCustomTag={addCustomTag}
        />
      )}

      {popupPost && (
        <PostPopup
          post={popupPost}
          list={popupList}
          onClose={()=>{ setPopupPost(null); setPopupList([]); }}
          onNavigate={async (np)=>{
            // Lazy-load the full post (with image) when navigating
            try { const r = await api.get(`/posts/${np.id}`); setPopupPost(r.data); }
            catch { setPopupPost(np); }
          }}
          onSave={async (p, closeAfter) => {
            await save(p);
            if (closeAfter) { setPopupPost(null); setPopupList([]); }
          }}
          onDelete={async ()=>{ await remove(popupPost.id); setPopupPost(null); setPopupList([]); }}
          tagOptions={allTagOptions}
          onAddCustomTag={addCustomTag}
        />
      )}

      {showBulk && <BulkImportModal mode="post" onClose={()=>setShowBulk(false)} onDone={load} />}
      <TemplatesModal open={showTemplates} onClose={()=>setShowTemplates(false)} onDone={load} />
    </div>
  );
}

function ViewBtn({ children, active, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} data-active={active ? "true" : "false"}>
      {children}
    </button>
  );
}

function CalendarView({ posts, month, setMonth, backlogOpen, setBacklogOpen, dragId, setDragId, onSelect, onOpenPopup, onPatch, onAdd, displayLabel, mediaCache }) {
  const [hoverDate, setHoverDate] = useState(null);

  const days = useMemo(() => {
    // Build the 6-week grid starting from Monday of the week containing day 1
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const startDay = (first.getDay() + 6) % 7; // Monday=0
    const start = new Date(first);
    start.setDate(first.getDate() - startDay);
    const arr = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [month]);

  const byDate = useMemo(() => {
    const m = new Map();
    for (const p of posts) {
      if (!p.date_to_post) continue;
      const key = p.date_to_post;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(p);
    }
    return m;
  }, [posts]);

  const monthLabel = month.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const todayISO = toISODate(new Date());
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);
  // Track the calendar's natural height so the backlog panel can match it
  // (backlog's inner list scrolls when its content exceeds this bound).
  const calRef = useRef(null);
  const [calHeight, setCalHeight] = useState(null);
  useEffect(() => {
    if (!calRef.current || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect?.height;
      if (h && Math.abs(h - (calHeight || 0)) > 1) setCalHeight(h);
    });
    ro.observe(calRef.current);
    return () => ro.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pickerOpen) return;
    const onDoc = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [pickerOpen]);

  const onDropOnDate = (e, dateISO) => {
    const id = dragId || (e && e.dataTransfer && e.dataTransfer.getData("text/post-id"));
    setDragId(null);
    setHoverDate(null);
    if (!id) return;
    onPatch(id, { date_to_post: dateISO });
  };

  const dayHeaders = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  return (
    // Calendar grows to its natural content height (bigger when a month has more
    // posts, smaller when it's light). The backlog aside then matches that height
    // via `calHeight` measured by the ResizeObserver above, so it never runs past
    // the bottom of the calendar and its inner list scrolls when it needs to.
    <div className="flex gap-4 items-start">
      <div ref={calRef} className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1">
            <button
              data-testid="cal-prev-year"
              onClick={()=>setMonth(new Date(month.getFullYear()-1, month.getMonth(), 1))}
              className="p-2 rounded-full hover:bg-[color:var(--paper)] text-xs font-mono"
              title="Previous year"
              style={{color:'var(--ink-3)'}}
            >«</button>
            <button
              data-testid="cal-prev"
              onClick={()=>setMonth(new Date(month.getFullYear(), month.getMonth()-1, 1))}
              className="p-2 rounded-full hover:bg-[color:var(--paper)]"
              title="Previous month"
            ><CaretLeft size={14}/></button>
            <button
              data-testid="cal-next"
              onClick={()=>setMonth(new Date(month.getFullYear(), month.getMonth()+1, 1))}
              className="p-2 rounded-full hover:bg-[color:var(--paper)]"
              title="Next month"
            ><CaretRight size={14}/></button>
            <button
              data-testid="cal-next-year"
              onClick={()=>setMonth(new Date(month.getFullYear()+1, month.getMonth(), 1))}
              className="p-2 rounded-full hover:bg-[color:var(--paper)] text-xs font-mono"
              title="Next year"
              style={{color:'var(--ink-3)'}}
            >»</button>
            <button
              data-testid="cal-today"
              onClick={()=>{ const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }}
              className="chip cursor-pointer ml-1"
            >Today</button>
          </div>

          {/* Click the month label to open a fast jump-to-month picker */}
          <div className="relative" ref={pickerRef}>
            <button
              type="button"
              data-testid="cal-month-label"
              onClick={()=>setPickerOpen(o=>!o)}
              className="font-serif text-2xl font-semibold hover:opacity-80 cursor-pointer inline-flex items-center gap-1"
              title="Jump to any month"
            >
              {monthLabel}
              <CaretRight size={14} style={{transform: pickerOpen ? "rotate(90deg)" : "rotate(0)", transition: "transform .15s"}}/>
            </button>
            {pickerOpen && (
              <div
                data-testid="cal-month-picker"
                className="absolute right-0 top-full mt-2 z-20 p-3 rounded-2xl shadow-lg"
                style={{background:'#fff', border:'1px solid var(--line)', minWidth: 260}}
              >
                <div className="flex items-center justify-between mb-2">
                  <button
                    type="button"
                    onClick={()=>setMonth(new Date(month.getFullYear()-1, month.getMonth(), 1))}
                    className="p-1 rounded-full hover:bg-[color:var(--paper)]"
                    data-testid="picker-prev-year"
                  ><CaretLeft size={12}/></button>
                  <div className="font-mono text-sm font-semibold" data-testid="picker-year">{month.getFullYear()}</div>
                  <button
                    type="button"
                    onClick={()=>setMonth(new Date(month.getFullYear()+1, month.getMonth(), 1))}
                    className="p-1 rounded-full hover:bg-[color:var(--paper)]"
                    data-testid="picker-next-year"
                  ><CaretRight size={12}/></button>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {monthNames.map((mn, mi) => {
                    const isCurrent = mi === month.getMonth();
                    return (
                      <button
                        key={mn}
                        type="button"
                        data-testid={`picker-month-${mn.toLowerCase()}`}
                        onClick={()=>{ setMonth(new Date(month.getFullYear(), mi, 1)); setPickerOpen(false); }}
                        className="chip cursor-pointer text-xs"
                        style={isCurrent ? {background:'var(--accent)', color:'#fff', borderColor:'var(--accent)'} : {}}
                      >{mn}</button>
                    );
                  })}
                </div>
                <div className="mt-3 pt-3 flex items-center justify-between gap-2 text-xs" style={{borderTop:'1px solid var(--line)'}}>
                  <button
                    type="button"
                    onClick={()=>{ const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); setPickerOpen(false); }}
                    className="link-underline"
                    data-testid="picker-jump-today"
                  >Today</button>
                  <button
                    type="button"
                    onClick={()=>{ const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth()+6, 1)); setPickerOpen(false); }}
                    className="link-underline"
                    data-testid="picker-jump-6mo"
                  >+6 months</button>
                  <button
                    type="button"
                    onClick={()=>{ const d = new Date(); setMonth(new Date(d.getFullYear()+1, d.getMonth(), 1)); setPickerOpen(false); }}
                    className="link-underline"
                    data-testid="picker-jump-1y"
                  >+1 year</button>
                </div>
              </div>
            )}
          </div>

          <div style={{width: 172}}/>
        </div>

        <div className="grid grid-cols-7 gap-2 mb-2">
          {dayHeaders.map(h => <div key={h} className="eyebrow text-center" style={{color:'var(--ink-3)'}}>{h}</div>)}
        </div>

        <div className="grid grid-cols-7 gap-2" data-testid="cal-grid">
          {days.map((d, i) => {
            const iso = toISODate(d);
            const inMonth = d.getMonth() === month.getMonth();
            const isToday = iso === todayISO;
            const dayPosts = byDate.get(iso) || [];
            const isHover = hoverDate === iso;
            return (
              <div
                key={i}
                data-testid={`cal-day-${iso}`}
                onDragOver={(e)=>{ e.preventDefault(); setHoverDate(iso); }}
                onDragLeave={()=>setHoverDate(h => h === iso ? null : h)}
                onDrop={(e)=>onDropOnDate(e, iso)}
                className="rounded-2xl p-2 min-h-[168px] flex flex-col gap-1 transition-all"
                style={{
                  background: isHover ? "var(--accent-tint)" : (inMonth ? "#fff" : "var(--paper)"),
                  border: `1px solid ${isHover ? "var(--accent)" : "var(--line)"}`,
                  opacity: inMonth ? 1 : 0.55,
                }}
              >
                <div className="flex items-center justify-between px-1">
                  <span className={`text-xs font-mono ${isToday ? "font-semibold" : ""}`} style={{color: isToday ? "var(--accent)" : "var(--ink-3)"}}>{d.getDate()}</span>
                  {dayPosts.length > 0 && <span className="text-[10px]" style={{color:'var(--ink-3)'}}>{dayPosts.length}</span>}
                </div>
                <div className="flex-1 space-y-1">
                  {dayPosts.map(p => (
                    <CalendarPostCard key={p.id} post={p} mediaCache={mediaCache} onClick={()=>onSelect(p)} onDragStart={()=>setDragId(p.id)} onDragEnd={()=>setDragId(null)} isDragging={dragId===p.id} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {backlogOpen && (
        <BacklogPanel posts={posts} onSelect={onSelect} onOpenPopup={onOpenPopup} setDragId={setDragId} dragId={dragId} onAdd={onAdd} onClose={()=>setBacklogOpen(false)} onPatch={onPatch} mediaCache={mediaCache} matchHeight={calHeight} />
      )}
    </div>
  );
}

function CalendarPostCard({ post, mediaCache, onClick, onDragStart, onDragEnd, isDragging }) {
  const hasImage = !!post.media_url || !!post.has_image;
  // Prefer any image already inlined on the post; otherwise fall back to the
  // lazy-loaded thumbnail we fetched in the CalendarView batch call.
  const imgSrc = post.media_url || (mediaCache && mediaCache[post.id]) || "";
  const isPlaceholder = !!post.is_placeholder;
  return (
    <div
      data-testid={`cal-post-${post.id}`}
      data-placeholder={isPlaceholder ? "1" : "0"}
      draggable
      onDragStart={(e)=>{ e.stopPropagation(); try { e.dataTransfer.setData("text/post-id", post.id); e.dataTransfer.effectAllowed = "move"; } catch {} onDragStart(); }}
      onDragEnd={(e)=>{ e.stopPropagation(); onDragEnd(); }}
      onClick={(e)=>{ e.stopPropagation(); onClick(); }}
      className="rounded-xl overflow-hidden cursor-move flex flex-col"
      style={{
        background: isDragging ? "var(--accent-tint)" : (isPlaceholder ? "transparent" : "var(--bg-soft)"),
        border: isPlaceholder
          ? "1px dashed var(--ink-4)"
          : "1px solid var(--line)",
        opacity: isPlaceholder ? 0.85 : 1,
      }}
      title={isPlaceholder ? `Placeholder — ${post.post_type_tag || "Untyped"}` : (post.topic || post.content)}
    >
      {hasImage && (
        imgSrc ? (
          <img src={imgSrc} alt="" className="w-full h-14 object-cover" />
        ) : (
          <div className="w-full h-14 flex items-center justify-center" style={{background:'var(--paper)',color:'var(--ink-4)'}}>
            <ImageSquare size={16}/>
          </div>
        )
      )}
      <div className="px-1.5 py-1 text-[11px] leading-tight space-y-0.5">
        {hasImage ? (
          // Image is present, show the post-type chip and channels below it.
          <>
            {post.post_type_tag && (
              <span className="chip inline-flex items-center" style={{padding:'2px 6px', fontSize:10, ...tagStyle(post.post_type_tag)}}>{post.post_type_tag}</span>
            )}
            {!post.post_type_tag && !(post.channels && post.channels.length) && (
              <div className="truncate" style={{color:'var(--ink-3)'}}>{post.topic || (post.content || "").split("\n")[0].slice(0, 30) || "Untitled"}</div>
            )}
            {post.channels && post.channels.length > 0 && (
              <div><ChannelChips channels={post.channels} size="sm"/></div>
            )}
          </>
        ) : (
          <>
            <div className="truncate font-medium">{post.topic || (post.content || "Untitled").split("\n")[0].slice(0, 40)}</div>
            {(post.post_type_tag || (post.channels && post.channels.length > 0)) && (
              <div className="flex items-center gap-1 flex-wrap">
                {post.post_type_tag && (
                  <span className="chip inline-flex items-center" style={{padding:'0px 5px', fontSize:9, ...tagStyle(post.post_type_tag)}}>{post.post_type_tag}</span>
                )}
                <ChannelChips channels={post.channels} size="sm"/>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function BacklogPanel({ posts, onSelect, onOpenPopup, setDragId, dragId, onAdd, onClose, onPatch, mediaCache, matchHeight }) {
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const backlog = useMemo(() => {
    const list = posts.filter(p => {
      // A post is in the backlog when it has no scheduled date AND has not been
      // published (published posts should never re-appear in the unscheduled list).
      if (p.date_to_post) return false;
      const s = (p.status || "").toLowerCase();
      if (s === "published") return false;
      return true;
    });
    const qq = q.trim().toLowerCase();
    return list.filter(p => {
      if (typeFilter !== "all" && (p.post_type_tag || "").toLowerCase() !== typeFilter.toLowerCase()) return false;
      if (!qq) return true;
      return (
        (p.topic || "").toLowerCase().includes(qq) ||
        (p.content || "").toLowerCase().includes(qq) ||
        (p.pillar || "").toLowerCase().includes(qq) ||
        (p.post_type_tag || "").toLowerCase().includes(qq)
      );
    });
  }, [posts, q, typeFilter]);

  const tagOptions = useMemo(() => {
    const s = new Set(DEFAULT_POST_TYPE_TAGS.map(x => x.toLowerCase()));
    posts.forEach(p => { if (p.post_type_tag) s.add(p.post_type_tag.toLowerCase()); });
    return Array.from(s);
  }, [posts]);

  const onDropUnschedule = (e) => {
    e.preventDefault();
    const id = dragId || (e.dataTransfer && e.dataTransfer.getData("text/post-id"));
    setDragId(null);
    if (!id) return;
    onPatch(id, { date_to_post: null });
  };

  return (
    <aside
      className="w-[320px] flex-shrink-0 card p-4 flex flex-col"
      data-testid="backlog-panel"
      style={{ height: matchHeight ? `${matchHeight}px` : undefined, minHeight: 0 }}
      onDragOver={(e)=>e.preventDefault()}
      onDrop={onDropUnschedule}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Stack size={14} style={{color:'var(--accent)'}}/>
          <div className="eyebrow">Backlog</div>
          <span className="text-xs" style={{color:'var(--ink-3)'}}>{backlog.length}</span>
        </div>
        <button data-testid="backlog-close" onClick={onClose} className="p-1.5 rounded-full hover:bg-[color:var(--paper)]"><X size={14}/></button>
      </div>
      <div className="text-xs mb-3" style={{color:'var(--ink-3)'}}>
        Unscheduled posts. Drag any card onto a day to schedule it. Drop back here to unschedule.
      </div>
      <input
        data-testid="backlog-search"
        className="input mb-2"
        placeholder="Search backlog…"
        value={q}
        onChange={e=>setQ(e.target.value)}
      />
      <div className="flex items-center gap-1 flex-wrap mb-3" data-testid="backlog-type-filters">
        <button onClick={()=>setTypeFilter("all")} className="chip text-xs cursor-pointer" style={typeFilter==='all' ? {background:'var(--accent)', color:'#fff', borderColor:'var(--accent)'} : {}}>All</button>
        {tagOptions.map(t => (
          <button
            key={t}
            data-testid={`backlog-filter-${t}`}
            onClick={()=>setTypeFilter(t)}
            className="chip text-xs cursor-pointer capitalize"
            style={typeFilter===t ? {background:'var(--accent)', color:'#fff', borderColor:'var(--accent)'} : tagStyle(t)}
          >{t}</button>
        ))}
      </div>
      <button data-testid="backlog-add" onClick={onAdd} className="btn btn-ghost text-xs mb-3"><Plus size={12}/> New idea</button>
      <div className="flex-1 min-h-0 overflow-y-auto scroll-thin -mx-1 px-1 space-y-2">
        {backlog.map(p => {
          const imgSrc = p.media_url || (mediaCache && mediaCache[p.id]) || "";
          return (
          <div
            key={p.id}
            data-testid={`backlog-${p.id}`}
            draggable
            onDragStart={(e)=>{ try { e.dataTransfer.setData("text/post-id", p.id); e.dataTransfer.effectAllowed = "move"; } catch {} setDragId(p.id); }}
            onDragEnd={()=>setDragId(null)}
            onClick={()=> onOpenPopup ? onOpenPopup(p, backlog) : onSelect(p)}
            className="rounded-2xl p-3 cursor-move fade-up"
            style={{ background: dragId===p.id ? "var(--accent-tint)" : "var(--bg-soft)", border: "1px solid var(--line)" }}
          >
            <div className="flex items-start gap-2 mb-1">
              {imgSrc ? (
                <img src={imgSrc} alt="" className="w-12 h-12 object-cover rounded-lg flex-shrink-0"/>
              ) : p.has_image ? (
                <div className="w-12 h-12 rounded-lg flex-shrink-0 animate-pulse" style={{background:'var(--paper)'}}/>
              ) : null}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{p.topic || (p.content || "Untitled").slice(0, 60)}</div>
                <div className="text-xs line-clamp-2 mt-0.5" style={{color:'var(--ink-3)'}}>{p.content || "No copy yet."}</div>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-wrap mt-1.5">
              {p.post_type_tag && <span className="chip" style={{padding:'2px 8px', fontSize:10, ...tagStyle(p.post_type_tag)}}>{p.post_type_tag}</span>}
              <ChannelChips channels={p.channels} size="sm"/>
              {p.pillar && <span className="chip" style={{padding:'2px 8px', fontSize:10}}>{p.pillar}</span>}
              <span className="chip" style={{padding:'2px 8px', fontSize:10}}>P{p.priority || 3}</span>
              <span className={`chip status-${p.status}`} style={{padding:'2px 8px', fontSize:10}}>{p.status}</span>
            </div>
          </div>
          );
        })}
        {backlog.length === 0 && (
          <div className="text-xs text-center py-8" style={{color:'var(--ink-3)'}}>
            No unscheduled posts. Add a new idea or drop a scheduled post back here to unschedule it.
          </div>
        )}
      </div>
    </aside>
  );
}

function PostDrawer({ post, onClose, onSave, onDelete, tagOptions, onAddCustomTag }) {
  const confirm = useConfirm();
  const [p, setP] = useState(post);
  const [uploading, setUploading] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestion, setSuggestion] = useState(null); // { tag, reason }
  const suggestTimer = useRef(null);
  const lastSuggested = useRef("");
  useEffect(()=>{
    // When opening a placeholder, strip the "[Placeholder] " prefix so the user
    // sees an empty topic field ready to be filled in.
    if (post && post.is_placeholder && (post.topic || "").startsWith("[Placeholder] ")) {
      setP({ ...post, topic: "" });
    } else {
      setP(post);
    }
    setSuggestion(null);
    lastSuggested.current = `${post?.topic||""}|${post?.content||""}`;
  }, [post]);
  const upd = (k, v) => setP({ ...p, [k]: v });

  const isNew = !!(p?._isNew || !p?.id);
  const isEmpty = !((p?.topic || "").trim()) && !((p?.content || "").trim()) && !(p?.media_url);
  const dirty = post && (
    (p.topic || "") !== (post.topic || "") ||
    (p.content || "") !== (post.content || "") ||
    (p.pillar || "") !== (post.pillar || "") ||
    (p.post_type_tag || "") !== (post.post_type_tag || "") ||
    (p.status || "Idea") !== (post.status || "Idea") ||
    (p.date_to_post || "") !== (post.date_to_post || "") ||
    (p.priority || 3) !== (post.priority || 3) ||
    (p.media_url || "") !== (post.media_url || "") ||
    (p.team_member || "") !== (post.team_member || "") ||
    JSON.stringify(p.channels || []) !== JSON.stringify(post.channels || [])
  );

  // Safe close: if this is a brand-new empty draft, discard silently. If the user
  // has typed something (new or existing) and there are unsaved edits, confirm before
  // throwing away work. Otherwise close normally.
  const requestClose = async () => {
    if (isNew && isEmpty) { onClose(); return; }
    if (dirty) {
      const ok = await confirm({
        title: "Discard unsaved changes?",
        description: "Your edits will be lost. Save first if you want to keep them.",
        confirmText: "Discard",
      });
      if (!ok) return;
    }
    onClose();
  };

  // Auto-suggest post_type_tag while the user types, only if none is set yet.
  // When a suggestion comes back we now auto-apply it (user wanted the suggestion
  // to be saved by default). The user can still overwrite by clicking another chip.
  const autoAppliedFor = useRef(""); // key of the last text we auto-applied for
  useEffect(() => {
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    const topic = (p?.topic || "").trim();
    const content = (p?.content || "").trim();
    const text = content.length >= 30 ? content : (topic.length >= 20 ? topic : "");
    if (!text) { setSuggestion(null); return; }
    // If a tag is set that DIDN'T come from our auto-apply for the current text,
    // clear any stale suggestion. But leave the suggestion visible when it matches
    // the currently-applied tag — that's the "Auto-tagged as X · Clear" confirmation.
    if (p.post_type_tag) {
      if (!suggestion || suggestion.tag.toLowerCase() !== p.post_type_tag.toLowerCase()) {
        setSuggestion(null);
      }
      return;
    }
    const key = `${p.topic||""}|${p.content||""}`;
    // Don't re-classify identical text we already auto-applied against.
    if (autoAppliedFor.current === key) return;
    suggestTimer.current = setTimeout(async () => {
      try {
        setSuggesting(true);
        const r = await api.post("/posts/classify-type", { text });
        lastSuggested.current = key;
        const tag = r.data?.tag;
        if (tag) {
          // Auto-apply the classifier's tag so the user doesn't have to click "Use this".
          // Guard with the ref so we don't loop when the effect re-runs after setting the tag.
          autoAppliedFor.current = key;
          setP(prev => (prev?.post_type_tag ? prev : { ...prev, post_type_tag: tag }));
          setSuggestion({ tag, reason: r.data.reason || "" });
        } else {
          setSuggestion(null);
        }
      } catch { setSuggestion(null); }
      finally { setSuggesting(false); }
    }, 900);
    return () => { if (suggestTimer.current) clearTimeout(suggestTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p?.topic, p?.content, p?.post_type_tag]);

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      // Image upload requires a persisted post id. If we're on an unsaved draft,
      // persist first and then attach the image to the returned id.
      let target = p;
      if (isNew) {
        const saved = await onSave(p, { keepOpen: true });
        if (!saved || !saved.id) throw new Error("Could not create draft before upload.");
        target = saved;
        setP(saved);
      }
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/posts/${target.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setP(r.data);
      toast.success("Image attached.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{background:'rgba(0,0,0,0.55)'}} onClick={requestClose}>
      <aside onClick={e=>e.stopPropagation()} className="w-full max-w-2xl h-full overflow-auto scroll-thin p-6" style={{background:'var(--bg-elev)'}} data-testid="post-drawer">
        <div className="flex justify-between items-center mb-6">
          <div className="eyebrow">{isNew ? "New post" : "Post detail"}</div>
          <button onClick={requestClose} className="p-2" data-testid="post-drawer-close"><X size={18}/></button>
        </div>

        <label className="block mb-4"><div className="eyebrow mb-2">Topic</div>
          <input data-testid="post-topic" className="input" value={p.topic||""} onChange={e=>upd("topic", e.target.value)} onPaste={onPasteNormalize((v)=>upd("topic", v))} />
        </label>
        <label className="block mb-4"><div className="eyebrow mb-2">Content</div>
          <textarea data-testid="post-content" className="textarea min-h-[220px]" value={p.content||""} onChange={e=>upd("content", e.target.value)} onPaste={onPasteNormalize((v)=>upd("content", v))} />
        </label>

        <div className="eyebrow mb-2">Image / media</div>
        <div className="border hairline-2 border-dashed p-4 mb-4">
          {p.media_url ? (
            <div className="flex gap-4 items-start">
              <img src={p.media_url} alt="" className="w-32 h-32 object-cover border hairline" />
              <div className="flex-1">
                <div className="text-sm">Attached, image reference for this post.</div>
                <button data-testid="post-image-remove" className="btn btn-ghost text-xs mt-3" onClick={()=>setP({...p, media_url:"", media_type:""})}>Remove</button>
              </div>
            </div>
          ) : (
            <label className="cursor-pointer text-sm flex items-center gap-3" data-testid="post-image-label">
              <input type="file" accept="image/*" className="hidden" data-testid="post-image-input" onChange={(e)=>{ const f=e.target.files?.[0]; if(f) uploadImage(f); e.target.value=""; }} />
              <div className="btn btn-ghost">{uploading ? "Uploading…" : "Attach image"}</div>
              <span style={{color:'var(--ink-3)'}}>PNG, JPG, WebP, up to 8MB</span>
            </label>
          )}
        </div>

        <div className="mb-4">
          <div className="eyebrow mb-2 flex items-center gap-2">
            <span>Post type</span>
            {suggesting && <span className="text-[10px] font-normal" style={{color:'var(--ink-3)'}}>· thinking…</span>}
          </div>
          {suggestion && p.post_type_tag && p.post_type_tag.toLowerCase() === suggestion.tag.toLowerCase() && (
            // Now that we auto-apply, the banner just confirms what we picked and
            // gives the user a one-click undo. They can also click any other chip.
            <div className="p-2 rounded-2xl mb-2 flex items-center gap-2 flex-wrap text-[11px]" style={{background:'var(--accent-tint)', border:'1px dashed var(--accent)'}} data-testid="post-type-suggestion">
              <span style={{color:'var(--ink-2)'}}>Auto-tagged as</span>
              <span className="chip" style={{padding:'2px 8px', ...tagStyle(suggestion.tag)}}>{suggestion.tag}</span>
              {suggestion.reason && <span style={{color:'var(--ink-3)'}}>{suggestion.reason}</span>}
              <button
                type="button"
                data-testid="post-type-suggestion-clear"
                className="ml-auto link-underline"
                onClick={() => upd("post_type_tag", "")}
              >Clear</button>
            </div>
          )}
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="post-type-tag-selector">
            {tagOptions.map(t => {
              const selected = (p.post_type_tag || "").toLowerCase() === t.toLowerCase();
              return (
                <button
                  key={t}
                  type="button"
                  data-testid={`post-type-tag-${t.toLowerCase()}`}
                  onClick={() => upd("post_type_tag", selected ? "" : t)}
                  className="chip text-xs cursor-pointer"
                  style={selected ? {background:'var(--accent)', color:'#fff', borderColor:'var(--accent)'} : tagStyle(t)}
                >{t}</button>
              );
            })}
            <button
              type="button"
              data-testid="post-type-tag-add"
              onClick={() => {
                const v = window.prompt("Add custom post type:");
                if (v && v.trim()) {
                  const clean = v.trim();
                  onAddCustomTag(clean);
                  upd("post_type_tag", clean);
                }
              }}
              className="chip text-xs cursor-pointer" style={{borderStyle:'dashed'}}
            >+ Custom</button>
          </div>
          {p.post_type_tag && !tagOptions.some(t => t.toLowerCase() === p.post_type_tag.toLowerCase()) && (
            <div className="text-xs mt-2" style={{color:'var(--ink-3)'}}>Current: <span className="chip text-xs" style={{padding:'2px 8px', ...tagStyle(p.post_type_tag)}}>{p.post_type_tag}</span></div>
          )}
        </div>

        <div className="mb-4">
          <div className="eyebrow mb-2">Channels <span className="text-[10px] font-normal" style={{color:'var(--ink-3)'}}>· cross-post to any</span></div>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="post-channels-selector">
            {DEFAULT_CHANNELS.map(ch => {
              const selected = (p.channels || []).some(c => c.toLowerCase() === ch.key.toLowerCase());
              return (
                <button
                  key={ch.key}
                  type="button"
                  data-testid={`post-channel-${ch.key.toLowerCase()}`}
                  onClick={() => {
                    const cur = p.channels || [];
                    const next = selected
                      ? cur.filter(c => c.toLowerCase() !== ch.key.toLowerCase())
                      : [...cur, ch.key];
                    upd("channels", next);
                  }}
                  className="chip text-xs cursor-pointer inline-flex items-center gap-1.5"
                  style={selected ? channelStyle(ch.key) : {}}
                  title={ch.label}
                >
                  <span className="font-mono" style={{fontSize:10}}>{ch.short}</span>
                  {ch.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <label className="block"><div className="eyebrow mb-2">Pillar</div>
            <select
              data-testid="post-pillar"
              className="select"
              value={p.pillar || ""}
              onChange={(e)=>{
                const v = e.target.value;
                if (v === "__new__") {
                  const next = window.prompt("New pillar name:");
                  if (next && next.trim()) {
                    const clean = next.trim();
                    upd("pillar", clean);
                    if (window.__blPillars && !window.__blPillars.includes(clean)) {
                      window.__blPillars = [...window.__blPillars, clean];
                    }
                  }
                } else {
                  upd("pillar", v);
                }
              }}
            >
              <option value="">— pick a pillar —</option>
              {(window.__blPillars || []).map((pn) => <option key={pn} value={pn}>{pn}</option>)}
              {p.pillar && !(window.__blPillars || []).includes(p.pillar) && (
                <option value={p.pillar}>{p.pillar}</option>
              )}
              <option value="__new__">+ Add new pillar…</option>
            </select>
          </label>
          <label className="block"><div className="eyebrow mb-2">Status</div>
            <select data-testid="post-status" className="select" value={p.status} onChange={e=>upd("status", e.target.value)}>
              {DEFAULT_STATUSES.map(s=><option key={s}>{s}</option>)}
            </select>
          </label>
          <label className="block"><div className="eyebrow mb-2">Date to post</div>
            <input data-testid="post-date" type="date" className="input" value={p.date_to_post||""} onChange={e=>upd("date_to_post", e.target.value)} />
          </label>
          <label className="block"><div className="eyebrow mb-2">Priority (1 highest - 5 lowest)</div>
            <select data-testid="post-priority" className="select" value={p.priority||3} onChange={e=>upd("priority", parseInt(e.target.value))}>
              {[1,2,3,4,5].map(n=><option key={n} value={n}>P{n}</option>)}
            </select>
          </label>
          <label className="block col-span-2"><div className="eyebrow mb-2">Team member</div>
            <input data-testid="post-team" className="input" value={p.team_member||""} onChange={e=>upd("team_member", e.target.value)} />
          </label>
        </div>

        <div className="eyebrow mb-2 mt-6">Analytics (manual entry)</div>
        <div className="grid grid-cols-4 gap-3 mb-6">
          {["impressions","reactions","comments","saves"].map(k => (
            <label key={k} className="block">
              <div className="text-xs mb-1 capitalize" style={{color:'var(--ink-3)'}}>{k}</div>
              <input data-testid={`post-${k}`} type="number" className="input" value={p[k]||0} onChange={e=>upd(k, parseInt(e.target.value||"0"))} />
            </label>
          ))}
        </div>

        <div className="flex justify-between  pt-4">
          <button onClick={onDelete} data-testid="post-delete" className="btn btn-ghost"><Trash size={14}/> {isNew ? "Discard" : "Delete"}</button>
          <button
            onClick={()=>onSave(p)}
            data-testid="post-save"
            disabled={isNew && isEmpty}
            className="btn btn-accent"
            style={(isNew && isEmpty) ? { opacity: 0.5, cursor: "not-allowed" } : {}}
            title={(isNew && isEmpty) ? "Add a topic or content first" : ""}
          >{isNew ? "Create post" : "Save changes"}</button>
        </div>
      </aside>
    </div>
  );
}

function PostPopup({ post, list, onClose, onNavigate, onSave, onDelete, tagOptions, onAddCustomTag }) {
  const confirm = useConfirm();
  const [p, setP] = useState(post);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { setP(post); }, [post?.id]);

  const idx = list.findIndex(x => x.id === post?.id);
  const canPrev = idx > 0;
  const canNext = idx >= 0 && idx < list.length - 1;
  const dirty = post && (
    (p.topic || "") !== (post.topic || "") ||
    (p.content || "") !== (post.content || "") ||
    (p.pillar || "") !== (post.pillar || "") ||
    (p.post_type_tag || "") !== (post.post_type_tag || "") ||
    (p.status || "") !== (post.status || "") ||
    (p.date_to_post || "") !== (post.date_to_post || "") ||
    (p.priority || 3) !== (post.priority || 3) ||
    (p.media_url || "") !== (post.media_url || "")
  );

  // Guard the backdrop / X / Esc close so accidentally clicking outside the popup
  // doesn't throw away pending edits.
  const requestClose = async () => {
    if (dirty) {
      const ok = await confirm({
        title: "Discard unsaved changes?",
        description: "Your edits will be lost. Save first if you want to keep them.",
        confirmText: "Discard",
      });
      if (!ok) return;
    }
    onClose();
  };

  const upd = (k, v) => setP(prev => ({ ...prev, [k]: v }));

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/posts/${p.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setP(r.data);
      toast.success("Image attached.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const saveAndStay = async () => { await onSave({ ...p, content: normalizePastedText(p.content || "") }, false); };
  const saveAndClose = async () => { await onSave({ ...p, content: normalizePastedText(p.content || "") }, true); };

  const navigate = async (dir) => {
    // Save silently if there are pending edits before moving
    if (dirty) { await onSave({ ...p, content: normalizePastedText(p.content || "") }, false); }
    const nextIdx = idx + dir;
    if (nextIdx < 0 || nextIdx >= list.length) return;
    onNavigate(list[nextIdx]);
  };

  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const isFormField = t && (t.tagName === "TEXTAREA" || t.tagName === "INPUT" || t.tagName === "SELECT" || t.isContentEditable);
      if (isFormField) return;
      if (e.key === "ArrowLeft" && canPrev) { e.preventDefault(); navigate(-1); }
      else if (e.key === "ArrowRight" && canNext) { e.preventDefault(); navigate(1); }
      else if (e.key === "Escape") { requestClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, canPrev, canNext, dirty, p]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-10 overflow-y-auto" style={{background:'rgba(0,0,0,0.55)'}} onClick={requestClose}>
      <div onClick={(e)=>e.stopPropagation()} className="card w-full max-w-3xl p-6 fade-up" data-testid="post-popup">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`chip status-${p.status}`}>{p.status}</span>
            {p.post_type_tag && <span className="chip text-xs" style={{padding:'2px 8px', ...tagStyle(p.post_type_tag)}}>{p.post_type_tag}</span>}
            <ChannelChips channels={p.channels} size="md"/>
            {p.pillar && <span className="chip text-xs">{p.pillar}</span>}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid="post-popup-prev"
              disabled={!canPrev}
              onClick={()=>navigate(-1)}
              className="p-2 rounded-full hover:bg-[color:var(--paper)] disabled:opacity-30"
              title="Previous (←)"
            ><CaretLeft size={16}/></button>
            <span className="text-xs font-mono" style={{color:'var(--ink-3)', minWidth: 60, textAlign:'center'}} data-testid="post-popup-counter">
              {idx + 1} of {list.length}
            </span>
            <button
              type="button"
              data-testid="post-popup-next"
              disabled={!canNext}
              onClick={()=>navigate(1)}
              className="p-2 rounded-full hover:bg-[color:var(--paper)] disabled:opacity-30"
              title="Next (→)"
            ><CaretRight size={16}/></button>
            <div className="w-px h-5 mx-1" style={{background:'var(--line)'}}/>
            <button type="button" onClick={requestClose} className="p-2 rounded-full hover:bg-[color:var(--paper)]" title="Close (Esc)"><X size={16}/></button>
          </div>
        </div>

        <label className="block mb-3">
          <div className="eyebrow mb-1.5">Topic</div>
          <input data-testid="post-popup-topic" className="input" value={p.topic || ""} onChange={e=>upd("topic", e.target.value)} onPaste={onPasteNormalize((v)=>upd("topic", v))} />
        </label>
        <label className="block mb-3">
          <div className="eyebrow mb-1.5">Content</div>
          <textarea
            data-testid="post-popup-content"
            className="textarea min-h-[260px]"
            value={p.content || ""}
            onChange={e=>upd("content", e.target.value)}
            onPaste={onPasteNormalize((v)=>upd("content", v))}
            style={{ fontSize: 16, lineHeight: 1.6 }}
          />
        </label>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <label className="block">
            <div className="eyebrow mb-1.5">Post type</div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {["Attract","Nurture","Position","Convert"].map(t => {
                const selected = (p.post_type_tag || "").toLowerCase() === t.toLowerCase();
                return (
                  <button
                    key={t}
                    type="button"
                    data-testid={`post-popup-type-${t.toLowerCase()}`}
                    onClick={()=>upd("post_type_tag", selected ? "" : t)}
                    className="chip text-xs cursor-pointer"
                    style={selected ? {background:'var(--accent)', color:'#fff', borderColor:'var(--accent)'} : tagStyle(t)}
                  >{t}</button>
                );
              })}
            </div>
          </label>
          <label className="block">
            <div className="eyebrow mb-1.5">Status</div>
            <select data-testid="post-popup-status" className="select" value={p.status || "Idea"} onChange={e=>upd("status", e.target.value)}>
              {DEFAULT_STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="eyebrow mb-1.5">Date to post</div>
            <input data-testid="post-popup-date" type="date" className="input" value={p.date_to_post || ""} onChange={e=>upd("date_to_post", e.target.value || null)} />
          </label>
          <label className="block">
            <div className="eyebrow mb-1.5">Priority</div>
            <select data-testid="post-popup-priority" className="select" value={p.priority || 3} onChange={e=>upd("priority", parseInt(e.target.value))}>
              {[1,2,3,4,5].map(n=><option key={n} value={n}>P{n}</option>)}
            </select>
          </label>
        </div>

        <div className="mb-3">
          <div className="eyebrow mb-1.5">Channels</div>
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="post-popup-channels">
            {DEFAULT_CHANNELS.map(ch => {
              const selected = (p.channels || []).some(c => c.toLowerCase() === ch.key.toLowerCase());
              return (
                <button
                  key={ch.key}
                  type="button"
                  data-testid={`post-popup-channel-${ch.key.toLowerCase()}`}
                  onClick={() => {
                    const cur = p.channels || [];
                    const next = selected
                      ? cur.filter(c => c.toLowerCase() !== ch.key.toLowerCase())
                      : [...cur, ch.key];
                    upd("channels", next);
                  }}
                  className="chip text-xs cursor-pointer inline-flex items-center gap-1.5"
                  style={selected ? channelStyle(ch.key) : {}}
                  title={ch.label}
                >
                  <span className="font-mono" style={{fontSize:10}}>{ch.short}</span>
                  {ch.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mb-3">
          <div className="eyebrow mb-1.5">Image / media</div>
          {p.media_url ? (
            <div className="flex gap-3 items-center">
              <img src={p.media_url} alt="" className="w-20 h-20 object-cover rounded-xl" style={{border:'1px solid var(--line)'}}/>
              <button data-testid="post-popup-image-remove" className="btn btn-ghost text-xs" onClick={()=>setP({...p, media_url:"", media_type:""})}>Remove image</button>
            </div>
          ) : (
            <label className="cursor-pointer text-sm flex items-center gap-3">
              <input type="file" accept="image/*" className="hidden" data-testid="post-popup-image-input" onChange={(e)=>{ const f=e.target.files?.[0]; if(f) uploadImage(f); e.target.value=""; }} />
              <div className="btn btn-ghost">{uploading ? "Uploading…" : "Attach image"}</div>
              <span className="text-xs" style={{color:'var(--ink-3)'}}>PNG, JPG, WebP, up to 8MB</span>
            </label>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t" style={{borderColor:'var(--line)'}}>
          <button onClick={onDelete} data-testid="post-popup-delete" className="btn btn-ghost" style={{color:"#b83a2b"}}><Trash size={14}/> Delete</button>
          <div className="flex items-center gap-2">
            <button type="button" onClick={requestClose} className="btn btn-ghost">Close</button>
            <button
              type="button"
              data-testid="post-popup-save"
              onClick={saveAndClose}
              disabled={!dirty}
              className="btn btn-accent"
              style={!dirty ? { opacity: 0.5 } : {}}
            ><Check size={14}/> {dirty ? "Save changes" : "Saved"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

